import { useState, useEffect } from 'react'

import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { secondaryCategoryRepository } from '@webapp/core/data-access/repositories/SecondaryCategoryRepository'
import { SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface ISecondaryCategorySelectProps extends IAsyncSelectProps<SecondaryCategory> {
  /** Filter secondary categories by primary category (ex. departments by location) */
  primaryCategoryId?: number
  /** Request secondary categories available to a user by ID */
  userId?: number
  /** Scope secondary categories to a legal entity */
  legalEntityId?: number
}

export const SecondaryCategorySelect = ({
  primaryCategoryId,
  userId,
  legalEntityId,
  ...props
}: ISecondaryCategorySelectProps) => {
  const intl = useProcurifyIntl()
  const [cacheOptions, setCacheOptions] = useState(false)

  useEffect(() => {
    setCacheOptions(false)
  }, [primaryCategoryId])

  const loadOptions: IAsyncSelectProps<SecondaryCategory>['loadOptions'] = (
    inputValue,
    pagination,
    callback
  ) => {
    secondaryCategoryRepository
      .list({
        primaryCategoryId,
        userId,
        legalEntityId,
        search: inputValue,
        page: pagination.page,
      })
      .then((res) => {
        callback({
          options: res.data.map(
            (secondaryCategory) => new SecondaryCategory(secondaryCategory)
          ),
          pagination: {
            hasMore: res.pagination
              ? res.pagination?.page < res.pagination?.numPages
              : false,
            page: pagination.page + 1,
          },
        })

        setCacheOptions(true)
      })
  }

  return (
    <AsyncSelect<SecondaryCategory>
      cacheOptions={cacheOptions}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      {...props}
    />
  )
}
